import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useRef } from "react";
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Avatar,
  Chip,
} from "@mui/material";
import Iconify from "../../components/iconify";
import Scrollbar from "../../components/scrollbar";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  formatCurrency,
  getStatusLabel,
  // sortTableData,
} from "src/utils/clubChallengesUtils";
import { del, get } from "src/utils/networkUtils";
import moment from "moment";
import ClubChallengeLeaderboardDialog from "../../components/challenge-admin/ClubChallengeLeaderboardDialog";
import useWithUrlTenant from "src/hooks/useTenant";


export default function ClubChallengesPage() {
  const withTenant = useWithUrlTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedClub, setSelectedClub] = useState("all");
  const [challengeSetups, setChallengeSetups] = useState([]);

  const [statusFilters, setStatusFilters] = useState({
    upcoming: true,
    live: true,
    open: true,
    finished: true,
    void: true,
    public: true,
    club: true,
    private: true,
  });
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const limit = 25;
  const [clubChallengeLeaderboardId, setClubChallengeLeaderboardId] =
    useState(null);

  const fetchChallengeSetups = async () => {
    try {
      const response = await get(withTenant('/admin/challenge-setups'));
      if (response.success && response.data) {
        setChallengeSetups(response.data);
      }
    } catch (error) {
      console.error('Error fetching challenge setups:', error);
    }
  };
  useEffect(() => {
    fetchChallengeSetups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withTenant]);
  const fetchChallengesWithFilters = useCallback(async (updatedFilters, currentOffset = 0) => {
    setLoading(true);

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit.toString());
      queryParams.append('offset', currentOffset.toString());

      // Add setupId filter - prioritize URL parameter over selectedClub
      const urlSetupId = searchParams.get('setupId');
      if (urlSetupId) {
        queryParams.append('setupId', urlSetupId);
      } else if (selectedClub !== 'all') {
        queryParams.append('setupId', selectedClub);
      }

      // Add status filters
      const statusList = [];
      if (updatedFilters.upcoming) statusList.push('upcoming');
      if (updatedFilters.live) statusList.push('live');
      if (updatedFilters.open) statusList.push('open');
      if (updatedFilters.finished) statusList.push('finished');
      if (updatedFilters.void) statusList.push('void');

      if (statusList.length > 0) {
        queryParams.append('status', statusList.join(','));
      }

      // Add challengeType filter
      const selectedTypes = ['public', 'club', 'private'].filter((type) => updatedFilters[type]);

      // All 3 selected or none selected → no param; 1 or 2 selected → comma-separated
      if (selectedTypes.length > 0 && selectedTypes.length < 3) {
        queryParams.append('challengeType', selectedTypes.join(','));
      }

      const response = await get(
        withTenant(`/admin/challenges?${queryParams.toString()}`)
      );

      setRows((prev) =>
        currentOffset === 0
          ? response.challenges
          : [...prev, ...response.challenges]
      );

      setHasMore(response.challenges.length === limit);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  }, [searchParams, selectedClub, limit, withTenant]);

  // Handle URL setupId parameter on mount
  useEffect(() => {
    const urlSetupId = searchParams.get('setupId');
    if (urlSetupId && challengeSetups.length > 0) {
      // Check if the setupId exists in challengeSetups
      const setupExists = challengeSetups.find(setup => +setup.clubId === +urlSetupId);

      if (setupExists) {
        setSelectedClub(urlSetupId);
      }
    }
  }, [searchParams, challengeSetups]);

  // Reset pagination when filters change
  useEffect(() => {
    setOffset(0);
    setRows([]);
    setHasMore(true);
  }, [statusFilters, selectedClub]);

  useEffect(() => {
    setLoading(true);
    fetchChallengesWithFilters(statusFilters, offset);
  }, [offset, searchParams, fetchChallengesWithFilters, statusFilters]);

  const handleOpenMenu = (event, data) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const lastChallengeRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + limit);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const handleStatusFilterChange = (status) => {
    const updatedFilters = {
      ...statusFilters,
      [status]: !statusFilters[status],
    };

    setStatusFilters(updatedFilters);
  };
  // No need for client-side filtering since server already filters by status and setupId
  const filteredRows = rows;

  const getStatusIndicator = (status) => {
    if (status === "live") {
      return (
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "#4CAF50",
            display: "inline-block",
            marginRight: 1,
          }}
        />
      );
    }
    return null;
  };


  const handleRemoveChallenge = async () => {
    try {
      await del(withTenant(`/admin/club-challenges/${openId}`));
      handleCloseMenu();
      setOffset(0);
      setHasMore(true);
      await fetchChallengesWithFilters(statusFilters, 0);
    } catch (e) {
      console.log(e);
    }
  };
  const formatCurrencyDisplay = (value) => {
    if (value === "--") return value;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <img
          src="/assets/icons/ufcoin.svg"
          alt="coin"
          style={{ width: 16, height: 16 }}
        />
        {formatCurrency(value)}
      </Box>
    );
  };
  const getStatusChip = (status) => {
    const statusConfig = {
      void: { color: "error", label: "Void" },
      upcoming: { color: "warning", label: "Upcoming" },
      open: { color: "success", label: "Open" },
      finished: { color: "info", label: "Finished" },
    };

    const config = statusConfig[status] || { color: "default", label: status };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="filled"
        sx={{
          fontWeight: 500,
          textTransform: "capitalize",
        }}
      />
    );
  };
  const getChallengeTypeChip = (type) => {
    const typeConfig = {
      0: {
        bgColor: "#2196f3",
        textColor: "#fff",
        label: "Club",
        icon: "eva:people-fill"
      },
      1: {
        bgColor: "#ff9800",
        textColor: "#fff",
        label: "Public",
        icon: "eva:globe-2-fill"
      },
      2: {
        bgColor: "#BF40BF",
        textColor: "#fff",
        label: "Private",
        icon: "eva:lock-fill"
      },
    };

    const config = typeConfig[type] || {
      bgColor: "#9e9e9e",
      textColor: "#fff",
      label: type,
      icon: "eva:question-mark-circle-fill"
    };

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          backgroundColor: config.bgColor,
          color: config.textColor,
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          minWidth: 'fit-content',
          width: 'fit-content',
        }}
      >
        <Iconify
          icon={config.icon}
          sx={{
            width: 14,
            height: 14,
          }}
        />
        {config.label}
      </Box>
    );
  };
  const calculateProfitMargin = (totalEntryFee, totalPrizePoolSize) => {
    if (!totalEntryFee || !totalPrizePoolSize) {
      return "--";
    }
    const calculate = (
      ((totalEntryFee - totalPrizePoolSize) / totalEntryFee) *
      100
    ).toFixed(1);
    return `${calculate} %`;
  };
  return (
    <>
      <Helmet>
        <title>Challenges</title>
      </Helmet>

      <Container>
        {/* Header Section */}
        <Box
          sx={{
            borderRadius: 2,
            p: 3,
            mb: 3,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#667eea" }}>
              Challenges
            </Typography >
          </Stack >
        </Box >

        {/* Filter Controls */}
        < Card sx={{ mb: 3, p: 2, background: "none", boxShadow: "none" }
        }>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={3}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Challenge Setup</InputLabel>

                <Select
                  sx={{
                    borderRadius: 5,
                    backgroundColor: "white",
                  }}
                  value={selectedClub}
                  label="All Setups"
                  onChange={(e) => setSelectedClub(e.target.value)}
                  endAdornment={<Iconify icon="eva:arrow-ios-downward-fill" />}
                >
                  <MenuItem value="all">All Setups</MenuItem>
                  {
                    challengeSetups.map((setup) => (
                      <MenuItem key={setup.clubId} value={setup.clubId}>
                        {setup.clubName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl >

              <Stack direction="row" spacing={2}>
                {Object.entries(statusFilters).map(([status, checked]) => (
                  <FormControlLabel
                    key={status}
                    control={
                      <Checkbox
                        checked={checked}
                        disabled={loading}
                        onChange={() => handleStatusFilterChange(status)}
                        sx={{
                          color: "#667eea",
                          "&.Mui-checked": {
                            color: "#667eea",
                          },
                        }}
                      />
                    }
                    label={getStatusLabel(status)}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        fontWeight: 500,
                        opacity: loading ? 0.5 : 1,
                      },
                    }}
                  />
                ))}
              </Stack>
            </Stack >

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => navigate(withTenant("/dashboard/challenge-setups"))}
              >
                View all Setups
              </Button >
            </Stack >
          </Stack >
        </Card >

        {/* Table */}
        < Card >
          {loading && rows?.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredRows?.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 4,
                color: "text.secondary",
              }}
            >
              <Iconify
                icon="eva:inbox-outline"
                sx={{ fontSize: 64, mb: 2, opacity: 0.5 }}
              />
              <Typography variant="h6" gutterBottom>
                No Club Challenges Found
              </Typography>
              <Typography variant="body2">
                Try adjusting your filters or create a new club challenge.
              </Typography>
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="club challenges table"
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                      <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Publish</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>Time Period</span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>Total Users</span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>Type</span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>Total Entries</span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>Prize Pool</span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>Profit</span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>Margin</span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>Is Paid</span>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600 }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows?.map((row, index) => (
                      <TableRow
                        key={index}
                        ref={index === rows.length - 1 ? lastChallengeRef : null}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:hover": { backgroundColor: "#f8f9fa" },
                        }}
                      >
                        <TableCell>{row.challengeId}</TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Avatar
                              src={row.iconUrl}
                              alt={row.challengeName}
                              sx={{ width: 32, height: 32 }}
                            />
                            <Typography variant="body2">
                              {row.challengeName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {getChallengeTypeChip(row?.challengeType)}
                        </TableCell>
                        <TableCell>
                          {row?.publishDate ? moment(row.publishDate).format("MMM DD, YYYY HH:mm") : "--"}
                        </TableCell>
                        <TableCell>
                          <Stack direction="column" spacing={0.5}>
                            {getStatusIndicator(row.status)}
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                            >
                              {row.startDate
                                ? moment(row.startDate).format(
                                  "MMM DD, YYYY HH:mm"
                                )
                                : "--"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.75rem",
                                color: "text.secondary",
                              }}
                            >
                              to{" "}
                              {row.endDate
                                ? moment(row.endDate).format(
                                  "MMM DD, YYYY HH:mm"
                                )
                                : "--"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{getStatusChip(row?.status)}</TableCell>
                        <TableCell>
                          {row?.userCount ?? "--"}{" "}
                          {`(min: ${row?.minUsers} max: ${row?.maxUsers})`}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.payoutFormat}
                            size="small"
                            sx={{
                              backgroundColor: "#4CAF50",
                              color: "white",
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {formatCurrencyDisplay(row.totalEntry ?? "--")}
                        </TableCell>
                        <TableCell>
                          {formatCurrencyDisplay(
                            row.prizePool.prizePoolSize ?? "--"
                          )}
                        </TableCell>
                        <TableCell>
                          {formatCurrencyDisplay(
                            row.totalEntry - row.prizePool.prizePoolSize ?? "--"
                          )}
                        </TableCell>
                        <TableCell>
                          {calculateProfitMargin(
                            row.totalEntry,
                            row.prizePool.prizePoolSize
                          ) ?? "--"}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Iconify
                              icon={
                                row.isPaid
                                  ? "eva:checkmark-circle-fill"
                                  : "eva:close-circle-fill"
                              }
                              sx={{
                                fontSize: 20,
                                color: row.isPaid ? "#4CAF50" : "#f44336",
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="large"
                            color="inherit"
                            onClick={(e) => handleOpenMenu(e, row)}
                            id={row.challengeId}
                          >
                            <Iconify icon="eva:more-vertical-fill" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )
          }

          {
            loading && rows?.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )
          }
        </Card >
      </Container >

      {/* Actions Menu */}
      < Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }
        }
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            navigate(withTenant(`/dashboard/challenges/${openId}`), {
              state: { openId },
            });
          }}
        >
          <Iconify icon="eva:eye-outline" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => setClubChallengeLeaderboardId(openId)}>
          <Iconify icon="eva:bar-chart-outline" sx={{ mr: 1 }} />
          Leaderboard
        </MenuItem>
        <MenuItem onClick={handleRemoveChallenge}>
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 1 }} />
          Remove Challenge
        </MenuItem>
      </Popover >

      {/* Leaderboard Dialog */}
      {
        clubChallengeLeaderboardId && (
          <ClubChallengeLeaderboardDialog
            open={true}
            onClose={() => setClubChallengeLeaderboardId(null)}
            clubChallengeLeaderboardId={clubChallengeLeaderboardId}
          />
        )
      }
    </>
  );
}
